import { defineStore } from 'pinia'
import { useRequest } from 'composables/useRequest.js'

const CACHE_TTL = 5 * 60 * 1000

export const RELEASE_TAGS = [
  { value: 'new', label: 'New' },
  { value: 'improved', label: 'Improved' },
  { value: 'fixed', label: 'Fixed' },
  { value: 'security', label: 'Security' },
]

// GET requests go through axios.get(url, { headers }) — query params must be
// embedded in the URL itself, they aren't sent from a request body.
function buildQuery(state) {
  const parts = [`page[number]=${state.page.number}`, `page[limit]=${state.page.limit}`]
  if (state.search) parts.push(`search=${encodeURIComponent(state.search)}`)
  if (state.status) parts.push(`status=${encodeURIComponent(state.status)}`)
  if (state.tag) parts.push(`tag=${encodeURIComponent(state.tag)}`)
  return parts.join('&')
}

// The changelog behind /release-notes. See
// app/controllers/api/v1/admin/release_notes_controller.rb.
//
// `nextVersions` is the whole point of the create form: the server computes
// what a major, minor and patch release would each be numbered, all three come
// back with the list, and the form labels its radio options with the real
// values instead of asking again on every click.
export const useAdminReleaseNotesStore = defineStore('adminReleaseNotes', {
  state: () => ({
    records: [],
    counts: { total: 0, draft: 0, scheduled: 0, published: 0 },
    nextVersions: { latest: null, latest_published_at: null, major: '1.0.0', minor: '1.0.0', patch: '1.0.0' },
    page: { current_page: 1, total_pages: 1, total_records: 0, number: 1, limit: 20 },
    search: '',
    status: '', // '' (all) | 'draft' | 'scheduled' | 'published'
    tag: '',
    fetchedAt: null,
    loading: false,
    error: null,
  }),

  getters: {
    isCacheValid: (state) =>
      state.fetchedAt !== null && Date.now() - state.fetchedAt < CACHE_TTL,
  },

  actions: {
    async fetchIfStale() {
      if (this.isCacheValid && this.records.length > 0) return
      await this.fetchList()
    },

    async fetchList() {
      this.loading = true
      this.error = null
      try {
        const { axiosRequest } = useRequest()
        const res = await axiosRequest('GET', `/api/v1/admin/release_notes?${buildQuery(this)}`, {}, false)
        if (!res.js_error && res.response.success) {
          this.records = res.response.data.records
          this.page = { ...this.page, ...res.response.data.page }
          this.counts = res.response.data.counts
          this.nextVersions = res.response.data.next_versions
          this.fetchedAt = Date.now()
        } else {
          this.error = 'Failed to load release notes.'
        }
      } catch {
        this.error = 'Something went wrong.'
      } finally {
        this.loading = false
      }
    },

    // Refreshed on its own after any write, because creating or deleting a
    // release changes what the next version would be for everything after it.
    async fetchNextVersions() {
      const { axiosRequest } = useRequest()
      const res = await axiosRequest('GET', '/api/v1/admin/release_notes/next_version', {}, false)
      if (!res.js_error && res.response.success) this.nextVersions = res.response.data
      return res
    },

    async create(payload) {
      const { axiosRequest } = useRequest()
      const res = await axiosRequest('POST', '/api/v1/admin/release_notes', { release_note: payload }, false)
      if (!res.js_error && res.response.success) this.invalidate()
      return res
    },

    // Multipart, so the body must be FormData and Content-Type must not be
    // set by hand — useRequest detects FormData and lets the browser write the
    // multipart boundary itself.
    async attachImage(id, file) {
      const { axiosRequest } = useRequest()
      const body = new FormData()
      body.append('file', file)
      return axiosRequest('POST', `/api/v1/admin/release_notes/${id}/attach_image`, body, false)
    },

    async update(id, payload) {
      const { axiosRequest } = useRequest()
      const res = await axiosRequest('PUT', `/api/v1/admin/release_notes/${id}`, { release_note: payload }, false)
      if (!res.js_error && res.response.success) this.applyRecord(res.response.data)
      return res
    },

    // `publishAt` in the future schedules the release. Nothing is enqueued —
    // the public scope filters on published_at <= now, so it goes live on its
    // own. See the controller.
    async publish(id, publishAt = null) {
      const { axiosRequest } = useRequest()
      const body = publishAt ? { publish_at: publishAt } : {}
      const res = await axiosRequest('PUT', `/api/v1/admin/release_notes/${id}/publish`, body, false)
      if (!res.js_error && res.response.success) {
        this.applyRecord(res.response.data)
        this.invalidate()
      }
      return res
    },

    async unpublish(id) {
      const { axiosRequest } = useRequest()
      const res = await axiosRequest('PUT', `/api/v1/admin/release_notes/${id}/unpublish`, {}, false)
      if (!res.js_error && res.response.success) {
        this.applyRecord(res.response.data)
        this.invalidate()
      }
      return res
    },

    // Drafts only — the server refuses a published release, which has a public
    // URL that may already be linked from elsewhere.
    async destroy(id) {
      const { axiosRequest } = useRequest()
      const res = await axiosRequest('DELETE', `/api/v1/admin/release_notes/${id}`, {}, false)
      if (!res.js_error && res.response.success) {
        this.records = this.records.filter((r) => r.id !== id)
        this.invalidate()
      }
      return res
    },

    applyRecord(updated) {
      const index = this.records.findIndex((r) => r.id === updated.id)
      if (index !== -1) this.records[index] = { ...this.records[index], ...updated }
    },

    invalidate() {
      this.fetchedAt = null
    },

    setSearch(val) {
      this.search = val
      this.page.number = 1
      this.invalidate()
    },

    setStatus(val) {
      this.status = val
      this.page.number = 1
      this.invalidate()
    },

    setTag(val) {
      this.tag = val
      this.page.number = 1
      this.invalidate()
    },

    goToPage(number) {
      this.page.number = number
      this.invalidate()
    },
  },
})
